import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Shield, Lock, Eye, EyeOff, AlertCircle, Loader2, CheckCircle, Mail, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Step 1: Request reset email ───────────────────────────────────────────
const RequestResetForm = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/password-reset-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Request failed'); }
      else { setDone(true); }
    } catch {
      setError('Network error. Please try again.');
    }
    setIsSubmitting(false);
  };

  if (done) {
    return (
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-green-500/10 rounded-2xl">
          <CheckCircle className="w-7 h-7 text-green-400" />
        </div>
        <h2 className="text-lg font-semibold text-white">Check your email</h2>
        <p className="text-slate-400 text-sm leading-relaxed">
          If <span className="text-white">{email}</span> is registered, you'll receive a reset link shortly. Check your spam folder if it doesn't arrive.
        </p>
        <Link to="/admin/login" className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold/80 transition-colors mt-4">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center mb-2">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/10 rounded-2xl mb-3">
          <Mail className="w-7 h-7 text-gold" />
        </div>
        <h2 className="text-lg font-semibold text-white">Forgot your password?</h2>
        <p className="text-slate-400 text-sm mt-1">Enter your admin email and we'll send a reset link.</p>
      </div>
      {error && (
        <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
      <input
        type="email" value={email} onChange={e => setEmail(e.target.value)} required
        placeholder="admin@example.com"
        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50 transition-all"
        data-testid="reset-email-input"
      />
      <button type="submit" disabled={isSubmitting}
        className="w-full py-3 bg-gold text-navy font-semibold rounded-xl hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        data-testid="reset-request-btn">
        {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send Reset Link'}
      </button>
      <Link to="/admin/login" className="block text-center text-sm text-slate-400 hover:text-gold transition-colors">
        ← Back to login
      </Link>
    </form>
  );
};

// ─── Step 2: Set new password via token ────────────────────────────────────
const SetNewPasswordForm = ({ token }) => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setIsSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword, confirm_password: confirmPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Reset failed'); }
      else { setDone(true); setTimeout(() => navigate('/admin/login'), 2500); }
    } catch {
      setError('Network error. Please try again.');
    }
    setIsSubmitting(false);
  };

  if (done) {
    return (
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-green-500/10 rounded-2xl">
          <CheckCircle className="w-7 h-7 text-green-400" />
        </div>
        <h2 className="text-lg font-semibold text-white">Password updated</h2>
        <p className="text-slate-400 text-sm">Redirecting you to login...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center mb-2">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/10 rounded-2xl mb-3">
          <Lock className="w-7 h-7 text-gold" />
        </div>
        <h2 className="text-lg font-semibold text-white">Set a new password</h2>
        <p className="text-slate-400 text-sm mt-1">Choose a strong password for your admin account.</p>
      </div>
      {error && (
        <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type={showPw ? 'text' : 'password'} value={newPassword}
          onChange={e => setNewPassword(e.target.value)} required minLength={6}
          placeholder="New password"
          className="w-full pl-11 pr-11 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50 transition-all"
          data-testid="reset-new-password"
        />
        <button type="button" onClick={() => setShowPw(!showPw)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
          {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type={showPw ? 'text' : 'password'} value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)} required
          placeholder="Confirm new password"
          className="w-full pl-11 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50 transition-all"
          data-testid="reset-confirm-password"
        />
      </div>
      <button type="submit" disabled={isSubmitting}
        className="w-full py-3 bg-gold text-navy font-semibold rounded-xl hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        data-testid="reset-submit-btn">
        {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating...</> : 'Update Password'}
      </button>
    </form>
  );
};

// ─── Page wrapper ──────────────────────────────────────────────────────────
const AdminResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <div className="min-h-screen bg-[#071B3A] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-gold" />
          </div>
          <p className="text-slate-400 text-sm">Third Culture Leadership — Admin Portal</p>
        </div>
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
          {token ? <SetNewPasswordForm token={token} /> : <RequestResetForm />}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPassword;
